import React, { useState, useEffect, useCallback } from "react";
import UploadZone from "../components/UploadZone";
import GalleryGrid from "../components/GalleryGrid";
import Countdown from "../components/Countdown";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

const WEDDING_DATE = new Date("2026-05-14T00:00:00");
const GALLERY_END = new Date("2026-12-22T23:59:59");

function isGalleryActive() {
    const now = new Date();
    return now >= WEDDING_DATE && now <= GALLERY_END;
}

function isBeforeWedding() {
    const now = new Date();
    return now < WEDDING_DATE;
}

function Gallery() {
    const [photos, setPhotos] = useState([]);
    const [slide, setSlide] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    useEffect(() => {
        const q = query(collection(db, "photos"), orderBy("uploadedAt", "desc"));
        const unsub = onSnapshot(q, (snap) => {
            const allPhotos = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

            // Pokaż tylko zdjęcia które są już skonwertowane
            // (nie wskazują na HEIC/HEIF/WEBP w URL)
            const readyPhotos = allPhotos.filter((photo) => {
                const url = photo.url || "";
                const fileName = photo.fileName || "";
                const isConverting =
                    fileName.toLowerCase().endsWith(".heic") ||
                    fileName.toLowerCase().endsWith(".heif") ||
                    url.includes(".heic") ||
                    url.includes(".heif");
                return !isConverting;
            });

            setPhotos(readyPhotos);
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        if (!playing || photos.length === 0) return;
        const interval = setInterval(() => {
            setSlide((s) => (s + 1) % photos.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [playing, photos.length]);

    const nextSlide = useCallback(() => {
        setSlide((s) => (s + 1) % Math.max(photos.length, 1));
    }, [photos.length]);

    const prevSlide = useCallback(() => {
        setSlide((s) => (s - 1 + Math.max(photos.length, 1)) % Math.max(photos.length, 1));
    }, [photos.length]);

    const togglePlay = () => setPlaying((p) => !p);

    const openLightbox = (index) => {
        setSlide(index);
        setLightboxOpen(true);
        setPlaying(false);
        document.body.style.overflow = "hidden";
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
        setPlaying(false);
        document.body.style.overflow = "";
    };

    useEffect(() => {
        if (!lightboxOpen) return;
        const handleKey = (e) => {
            if (e.key === "ArrowRight") nextSlide();
            if (e.key === "ArrowLeft")  prevSlide();
            if (e.key === "Escape")     closeLightbox();
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [lightboxOpen, nextSlide, prevSlide]);

    const formatDate = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString("pl-PL", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleDownload = async (url, fileName) => {
        try {
            const response = await fetch(url, { mode: "cors" });
            if (!response.ok) throw new Error("Błąd pobierania");
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName || "wesele.jpg";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        } catch (error) {
            console.error("Błąd:", error);
        }
    };

    if (isBeforeWedding()) {
        return (
            <div className="gallery-page">
                <div className="gallery-waiting">
                    <div className="waiting-icon">📸</div>
                    <h2 className="waiting-title">
                        Galeria otworzy się<br />
                        <em>14 sierpnia 2026</em>
                    </h2>
                    <p className="waiting-sub">
                        Wróć tu dzień przed weselem i dziel się<br />
                        wspomnieniami z Klaudią i Bartłomiejem!
                    </p>
                    <div className="waiting-countdown">
                        <Countdown />
                    </div>
                </div>
                <footer className="site-footer">
                    <div className="footer-names">Klaudia <span>&</span> Bartłomiej</div>
                    <div className="footer-date">15 · 08 · 2026 · Hotel & Restauracja President</div>
                    <div className="footer-fruits">🍋 🧡 🍊 💛 🌿</div>
                </footer>
            </div>
        );
    }

    if (!isGalleryActive() && !isBeforeWedding()) {
        return (
            <div className="gallery-page">
                <div className="gallery-closed">
                    <div className="closed-icon">💍</div>
                    <h2 className="closed-title">
                        Dziękujemy za<br />
                        <em>wspólne chwile!</em>
                    </h2>
                    <p className="closed-sub">
                        Galeria była dostępna przez 30 dni po weselu.<br />
                        Mamy nadzieję że zachowaliście piękne wspomnienia! 🌸
                    </p>
                    <div className="closed-hearts">🍋 🧡 🍊 💛 🌿</div>
                </div>
                <footer className="site-footer">
                    <div className="footer-names">Klaudia <span>&</span> Bartłomiej</div>
                    <div className="footer-date">15 · 08 · 2026 · Hotel & Restauracja President</div>
                    <div className="footer-fruits">🍋 🧡 🍊 💛 🌿</div>
                </footer>
            </div>
        );
    }

    return (
        <div className="gallery-page">

            {/* NAGŁÓWEK */}
            <div className="gal-header">
                <div>
                    <div className="gal-live-badge">
                        <span className="live-indicator"></span>
                        Live — aktualizuje się na bieżąco
                    </div>
                    <h1 className="gal-title">
                        Galeria<br /><em>Wspomnień</em>
                    </h1>
                    <div className="gal-subtitle">
                        Klaudia & Bartłomiej · 15.08.2026
                    </div>
                </div>
                <div className="gal-stats">
                    <div className="gal-stat">
                        <span className="gal-stat-num">{photos.filter(p => p.type !== "video").length}</span>
                        <span className="gal-stat-label">Zdjęć</span>
                    </div>
                    <div className="gal-stat">
                        <span className="gal-stat-num">{photos.filter(p => p.type === "video").length}</span>
                        <span className="gal-stat-label">Filmików</span>
                    </div>
                    <div className="gal-stat">
            <span className="gal-stat-num">
              {photos.reduce((sum, p) => sum + (p.downloads || 0), 0)}
            </span>
                        <span className="gal-stat-label">Pobrań</span>
                    </div>
                </div>
            </div>

            {/* UPLOAD */}
            <UploadZone />

            {/* SLIDESHOW BAR */}
            <div className="slide-bar">
                <span className="slide-lbl">Slideshow</span>
                <button className="slide-btn" onClick={prevSlide}>◀</button>
                <button
                    className="slide-play-btn"
                    onClick={() => {
                        if (photos.length === 0) return;
                        openLightbox(slide);
                        setPlaying(true);
                    }}
                >
                    {playing ? "⏸ Pause" : "▶ Play"}
                </button>
                <button className="slide-btn" onClick={nextSlide}>▶</button>
                <div className="slide-dots">
                    {photos.slice(0, 8).map((photo, i) => (
                        <div
                            key={photo.id}
                            className={`slide-thumb ${slide === i ? "active" : ""}`}
                            onClick={() => openLightbox(i)}
                        >
                            {photo.type === "video" ? (
                                <div className="slide-thumb-video">▶</div>
                            ) : (
                                <img src={photo.url} alt="" />
                            )}
                        </div>
                    ))}
                    {photos.length === 0 && (
                        <span className="slide-empty">Brak zdjęć — dodaj pierwsze!</span>
                    )}
                </div>
            </div>

            {/* SIATKA ZDJĘĆ */}
            <GalleryGrid
                key={photos.map(p => p.url).join(",")}
                onPhotoClick={openLightbox}
                photos={photos}
            />

            {/* LIGHTBOX */}
            {lightboxOpen && photos.length > 0 && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>

                        {/* Zamknij */}
                        <button className="lightbox-close" onClick={closeLightbox}>✕</button>

                        {/* Licznik */}
                        <div className="lightbox-counter">
                            {slide + 1} / {photos.length}
                        </div>

                        {/* Zdjęcie lub filmik */}
                        <div className="lightbox-img-wrap">
                            {photos[slide]?.type === "video" ? (
                                <video
                                    key={photos[slide]?.url}
                                    src={photos[slide]?.url}
                                    className="lightbox-img"
                                    controls
                                    autoPlay
                                    playsInline
                                    preload="auto"
                                    style={{ background: "#000" }}
                                />
                            ) : (
                                <img
                                    src={photos[slide]?.url}
                                    alt={photos[slide]?.fileName}
                                    className="lightbox-img"
                                />
                            )}
                        </div>

                        {/* Info */}
                        <div className="lightbox-info">
                            <div className="lightbox-author">
                                {photos[slide]?.type === "video" ? "🎥" : "📸"} {photos[slide]?.author || "Gość"}
                            </div>
                            <div className="lightbox-date">
                                {formatDate(photos[slide]?.uploadedAt)}
                            </div>
                            <div className="lightbox-category">
                                {photos[slide]?.category === "Ceremonia" ? "⛪" :
                                    photos[slide]?.category === "Przyjęcie" ? "🥂" : "📸"}
                                {photos[slide]?.category || "Inne"}
                            </div>
                        </div>

                        {/* Przyciski */}
                        <div className="lightbox-actions">
                            <button
                                className="lightbox-download"
                                onClick={() => handleDownload(photos[slide]?.url, photos[slide]?.fileName)}
                            >
                                ⬇ Pobierz
                            </button>
                            <button
                                className="lightbox-play"
                                onClick={togglePlay}
                            >
                                {playing ? "⏸ Pause" : "▶ Slideshow"}
                            </button>
                        </div>

                        {/* Strzałki */}
                        <button className="lightbox-prev" onClick={prevSlide}>❮</button>
                        <button className="lightbox-next" onClick={nextSlide}>❯</button>

                        {/* Miniaturki */}
                        <div className="lightbox-thumbs">
                            {photos.map((photo, i) => (
                                <div
                                    key={photo.id}
                                    className={`lightbox-thumb ${slide === i ? "active" : ""}`}
                                    onClick={() => setSlide(i)}
                                >
                                    {photo.type === "video" ? (
                                        <div className="lightbox-thumb-video">▶</div>
                                    ) : (
                                        <img src={photo.url} alt="" />
                                    )}
                                </div>
                            ))}
                        </div>

                    </div>
                </div>
            )}

            <footer className="site-footer">
                <div className="footer-names">Klaudia <span>&</span> Bartłomiej</div>
                <div className="footer-date">15 · 08 · 2026 · Hotel & Restauracja President</div>
                <div className="footer-fruits">🍋 🧡 🍊 💛 🌿</div>
            </footer>

        </div>
    );
}

export default Gallery;